import React, { useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Alert,
  Chip,
  Divider
} from '@mui/material';

import { useAgent } from '../contexts/AgentContext';
import { useNotification } from '../contexts/NotificationContext';

const AgentTestPage = () => {
  const {
    currentAgent,
    agentStatus,
    loading,
    loginAgent,
    logoutAgent,
    changeAgentStatus,
    syncAgentStatus,
    heartbeat
  } = useAgent();

  const { showSuccess, showError, showInfo } = useNotification();

  const [testAgent, setTestAgent] = useState('');
  const [testName, setTestName] = useState('');
  const [testExtension, setTestExtension] = useState('');
  const [newStatus, setNewStatus] = useState('AVAILABLE');

  const handleLogin = async () => {
    if (!testAgent || !testName || !testExtension) {
      showError('Por favor, completa todos los campos');
      return;
    }

    const agentData = {
      agent: testAgent,
      name: testName,
      extension: testExtension
    };

    const success = await loginAgent(agentData);
    if (success) {
      showSuccess(`Agente ${testAgent} logueado correctamente`);
    } else {
      showError('Error al loguear el agente');
    }
  };

  const handleLogout = async () => {
    const success = await logoutAgent();
    if (success) {
      showSuccess('Agente deslogueado correctamente');
    } else {
      showError('Error al desloguear el agente');
    }
  };

  const handleChangeStatus = async () => {
    const success = await changeAgentStatus(newStatus);
    if (success) {
      showSuccess(`Estado cambiado a ${newStatus}`);
    } else {
      showError('Error al cambiar el estado');
    }
  };

  const handleSyncStatus = async () => {
    const status = await syncAgentStatus();
    if (status) {
      showSuccess(`Estado sincronizado: ${status}`);
    } else {
      showError('Error al sincronizar el estado');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'AVAILABLE': 'success',
      'OFFLINE': 'default',
      'PAUSE': 'warning',
      'BUSY': 'error',
      'NOTREADY': 'secondary',
      'NO_NETWORK': 'error'
    };
    return colors[status] || 'default';
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Prueba de Sistema de Heartbeat de Agentes
      </Typography>


      <Grid container spacing={3}>
        {/* Estado Actual del Agente */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Estado Actual del Agente" />
            <CardContent>
              {currentAgent ? (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    <strong>Agente:</strong> {currentAgent.agent}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Nombre:</strong> {currentAgent.name}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Extensión:</strong> {currentAgent.extension}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Estado:</strong>
                    <Chip
                      label={agentStatus}
                      color={getStatusColor(agentStatus)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Heartbeat activo:</strong> {heartbeat ? 'Sí' : 'No'}
                  </Typography>
                  {loading && (
                    <Typography color="text.secondary">
                      Procesando...
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography color="text.secondary">
                  No hay agente logueado
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Login de Agente */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Login de Agente" />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="ID del Agente"
                  value={testAgent}
                  onChange={(e) => setTestAgent(e.target.value)}
                  size="small"
                  disabled={!!currentAgent}
                />
                <TextField
                  label="Nombre"
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  size="small"
                  disabled={!!currentAgent}
                />
                <TextField
                  label="Extensión"
                  value={testExtension}
                  onChange={(e) => setTestExtension(e.target.value)}
                  size="small"
                  disabled={!!currentAgent}
                />

                <Box sx={{ display: 'flex', gap: 1 }}>
                  {!currentAgent ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleLogin}
                      disabled={loading}
                      fullWidth
                    >
                      Loguear Agente
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="error"
                      onClick={handleLogout}
                      disabled={loading}
                      fullWidth
                    >
                      Desloguear Agente
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Gestión de Estado */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Gestión de Estado" />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    select
                    label="Nuevo Estado"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    size="small"
                    disabled={!currentAgent || loading}
                    SelectProps={{
                      native: true,
                    }}
                    sx={{ minWidth: 200 }}
                  >
                    <option value="AVAILABLE">Disponible</option>
                    <option value="PAUSE">En Pausa</option>
                    <option value="BUSY">Ocupado</option>
                    <option value="NOTREADY">No Disponible</option>
                    <option value="OFFLINE">Desconectado</option>
                  </TextField>

                  <Button
                    variant="contained"
                    onClick={handleChangeStatus}
                    disabled={!currentAgent || loading}
                  >
                    Cambiar Estado
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={handleSyncStatus}
                    disabled={!currentAgent || loading}
                  >
                    Sincronizar Estado
                  </Button>
                </Box>

                <Divider />

                <Typography variant="h6">Estados Disponibles:</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['AVAILABLE', 'PAUSE', 'BUSY', 'NOTREADY', 'OFFLINE', 'NO_NETWORK'].map((status) => (
                    <Chip
                      key={status}
                      label={status}
                      color={getStatusColor(status)}
                      size="small"
                      variant={status === agentStatus ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AgentTestPage;